import { useLazyQuery } from "@apollo/client";
import { IonCard } from "@ionic/react";
import { useEffect, useMemo } from "react";

import styles from "@components/ClientAvatar.module.css";
import Tooltip from "@components/Tooltip";
import {
  GetJourneyWithClientsDocument,
  GetJourneyWithClientsQuery,
  GetJourneyWithClientsQueryVariables
} from "@typing/Generated";
import { Colors } from "@utils/colorUtils";
import { sortByTimestamp } from "@utils/modelUtils";

type Props = {
  bold?: boolean;
  clientId: string | null;
  color?: Colors;
  firstName: string;
  fontSize?: number;
  journeyId: string | null;
  lastName?: string;
  overrideIndex?: number;
  size: number;
};

const ClientAvatar = ({
  bold = false,
  clientId,
  color,
  firstName,
  fontSize = 14,
  journeyId,
  lastName,
  overrideIndex,
  size
}: Props) => {
  const wrapperStyles = [styles.avatar];

  if (bold) {
    wrapperStyles.push("bold");
  }

  const letters = lastName ? firstName[0] + lastName[0] : firstName[0];
  const [getJourneyWithClientsQuery, journeyWithClientsQuery] = useLazyQuery<
    GetJourneyWithClientsQuery,
    GetJourneyWithClientsQueryVariables
  >(GetJourneyWithClientsDocument);

  useEffect(() => {
    if (journeyId && !overrideIndex) {
      getJourneyWithClientsQuery({ variables: { id: journeyId } });
    }
  }, [getJourneyWithClientsQuery, journeyId, overrideIndex]);

  const avatarColor = useMemo(() => {
    const colorOptions: Colors[] = [
      "primary-500",
      "secondary-100",
      "secondary",
      "primary",
      "secondary-500",
      "gray-100",
      "primary-300"
    ];

    if (clientId && (journeyWithClientsQuery.data?.journey.clientRoles || overrideIndex)) {
      const clientIndex = journeyWithClientsQuery.data?.journey.clientRoles
        ? sortByTimestamp(journeyWithClientsQuery.data?.journey.clientRoles, r => r.createdAt)
            .reverse()
            .findIndex(role => role.client.id === clientId)
        : overrideIndex;

      if (clientIndex !== undefined && clientIndex >= 0) {
        return colorOptions[clientIndex % colorOptions.length];
      }
    }

    if (color) {
      return color;
    }

    return "primary-500";
  }, [clientId, color, journeyWithClientsQuery.data?.journey.clientRoles, overrideIndex]);

  return (
    <Tooltip content={firstName + " " + (lastName ?? "")}>
      {tooltipTriggerProps => (
        <IonCard
          className={wrapperStyles.join(" ")}
          color={avatarColor}
          data-test="client-avatar"
          style={{ height: size, width: size }}
          {...tooltipTriggerProps}
        >
          <span className={styles.copy} style={{ fontSize }}>
            {letters?.toUpperCase()}
          </span>
        </IonCard>
      )}
    </Tooltip>
  );
};

export default ClientAvatar;
